import { useAuth0 } from "@auth0/auth0-react";
import { AxiosError } from "axios";
import Loading from "components/Loading";
import { useCallback, useEffect } from "react";
import { getUserByAuth0Id } from "requests/users";
import { useAlyviaUserStore } from "store/useAlyviaUseStore";
import useAuthStore from "store/useAuthStore";
import { isJwtValid, redirectToAlyviaSSO } from "utils";
import { alyviaAuth0Audience, JWT_TOKEN_KEY } from "utils/constants";
import showSnackbar from "utils/showSnackbar";

export default function Auth0Callback() {
    const { user: auth0User, isAuthenticated, getAccessTokenSilently, isLoading } = useAuth0();
    const { alyviaUser, setUser } = useAlyviaUserStore()
    const { setJwt, jwt, resetJwt } = useAuthStore()

    const onVerifyUser = useCallback(async (onSucess: VoidFunction, onFailed: VoidFunction) => {
        getUserByAuth0Id(auth0User?.sub || '').then((fetchedAlyviaUser) => {
            if (fetchedAlyviaUser?.accountId) {
                setUser(fetchedAlyviaUser)
                onSucess()
            } else {
                console.error(`User with id ${fetchedAlyviaUser.id} does not have an account!`)
                showSnackbar('Usuário não possui conta!', 'error', 3000)
                setTimeout(() => onFailed(), 2000)
            }
        }).catch((error: AxiosError) => {
            console.error(error)
            showSnackbar(`Algo deu errado ao buscar o seu usuário! ERR: ${error?.message}`, 'error', 6000)
        })
    }, [auth0User, setUser])

    const redirectToMain = () => {
        const timeoutId = setTimeout(() => {
            window.location.href = '/main'
        }, 300)

        return () => {
            clearTimeout(timeoutId)
        }
    }

    const defineJwtToken = useCallback(async () => {
        await getAccessTokenSilently({
            authorizationParams: {
                audience: alyviaAuth0Audience
            },
            cacheMode: 'off'
        }).then((token) => {
            setJwt(token)
        }).catch((error) => {
            showSnackbar(`Algo deu errado ao recuperar seu token no Auth0. ERR:${error?.message}`, 'error')
            console.error(error)
        })
    }, [getAccessTokenSilently, setJwt])

    useEffect(() => {
        if (!isLoading) {
            if (!isAuthenticated && !auth0User && !isLoading) {
                showSnackbar('Faça Login Primeiro!', 'warning')
                resetJwt()
                redirectToAlyviaSSO()
                return;
            }
        }
    }, [auth0User, isAuthenticated, jwt, alyviaUser, isLoading, resetJwt])

    useEffect(() => {
        if (!isLoading && isAuthenticated && auth0User && (!jwt || !isJwtValid(jwt))) {
            defineJwtToken()
        }
    }, [isAuthenticated, auth0User, isLoading, defineJwtToken, jwt]);

    useEffect(() => {
        if (jwt && isJwtValid(jwt)) {
            const intervalId = setInterval(() => {
                const storedJwt = localStorage.getItem(JWT_TOKEN_KEY);
                if (storedJwt === jwt) {
                    onVerifyUser(redirectToMain, () => redirectToAlyviaSSO())
                    clearInterval(intervalId)
                }
            }, 1000)

            return () => {
                clearInterval(intervalId);
            };
        }
    }, [jwt, onVerifyUser, setJwt])

    return <Loading />
}
